import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime, parseServerDate } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error as ErrorView } from "@ui/error/Error";
import { IconDownload } from "@ui/icons/IconDownload";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Spinner } from "@ui/spinner/Spinner";
import { Table } from "@ui/table/Table";
import { Typo } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import FileSaver from "file-saver";
import Signal from "badmfck-signal";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { prepareFileDownloadBlob } from "src/utils/fileDownload";
import { FileRecordPreview } from "./FileRecordPreview";

export type TDectaFilePopupRequest = {
	name: string;
	path: string;
	previewable: boolean;
	load: () => Promise<Blob>;
};

type TLoadedFileState = {
	key: string;
	blob: Blob | null;
	text: string;
};

type TParsedCsv = {
	headers: string[];
	rows: Record<string, string>[];
	delimiter: string;
};

type TReportCard = {
	title: string;
	meta: string[];
	body: string;
};

type TDataRowsPreview = {
	rows: Record<string, unknown>[];
	footerRows: Record<string, unknown>[];
	footerType?: string;
	meta: Record<string, string>;
	headers: string[];
};

type TZipEntry = {
	name: string;
	type: "file" | "directory";
	size: number;
	compressedSize: number;
	modified: Date | null;
	compression: string;
};

const viewerColors = {
	border: "#e5e7eb",
	header: "#f8fafc",
	muted: "#64748b",
	text: "#0f172a",
};

export const S_DECTA_FILES_POPUP = new Signal<TDectaFilePopupRequest | null>("S_DECTA_FILES_POPUP");

const getError = (message: string): IError => ({ code: 0, message });

const getFileExtension = (fileName: string) => {
	const lower = fileName.toLowerCase();
	if (lower.endsWith(".txt-simple")) return "txt-simple";
	return lower.split(".").pop() ?? "";
};

const isPdfFile = (fileName: string) => getFileExtension(fileName) === "pdf";
const isZipFile = (fileName: string) => getFileExtension(fileName) === "zip";

const readBlobAsText = async (blob: Blob) => {
	try {
		return await blob.text();
	} catch {
		throw new Error(msg("cannotReadFileContent"));
	}
};

const getRequestKey = (request: TDectaFilePopupRequest) => `${request.path}::${request.name}`;

const formatBytes = (value: number) => {
	if (!Number.isFinite(value)) return "";
	if (value >= 1024 * 1024) return msg("templateMb_016aac3b", { v0: (value / (1024 * 1024)).toFixed(2) });
	if (value >= 1024) return msg("templateKb_f1dd622b", { v0: (value / 1024).toFixed(2) });
	return `${value} B`;
};

const decodeZipString = (bytes: Uint8Array, utf8: boolean) => {
	if (!bytes.length) return "";
	try {
		return new TextDecoder(utf8 ? "utf-8" : "windows-1252").decode(bytes);
	} catch {
		return Array.from(bytes)
			.map(byte => String.fromCharCode(byte))
			.join("");
	}
};

const getDosDateTime = (time: number, date: number) => {
	const day = date & 0x1f;
	const month = (date >> 5) & 0x0f;
	const year = ((date >> 9) & 0x7f) + 1980;
	const second = (time & 0x1f) * 2;
	const minute = (time >> 5) & 0x3f;
	const hour = (time >> 11) & 0x1f;
	const pad = (value: number) => String(value).padStart(2, "0");
	// DECTA archive timestamps follow the server UTC convention.
	return parseServerDate(`${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}Z`);
};

const getCompressionName = (method: number) => {
	if (method === 0) return "Stored";
	if (method === 8) return "Deflated";
	return msg("templateMethod_b2e97054", { v0: method });
};

const parseZipEntries = async (blob: Blob): Promise<TZipEntry[]> => {
	const buffer = await blob.arrayBuffer();
	const view = new DataView(buffer);
	const bytes = new Uint8Array(buffer);
	const eocdSignature = 0x06054b50;
	const centralSignature = 0x02014b50;
	const maxCommentLength = Math.min(buffer.byteLength, 0xffff + 22);
	let eocdOffset = -1;

	for (let offset = buffer.byteLength - 22; offset >= buffer.byteLength - maxCommentLength; offset--) {
		if (offset < 0) break;
		if (view.getUint32(offset, true) === eocdSignature) {
			eocdOffset = offset;
			break;
		}
	}

	if (eocdOffset === -1) throw new Error(msg("zipCentralDirectoryWasNotFound"));

	const entriesCount = view.getUint16(eocdOffset + 10, true);
	const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true);
	const entries: TZipEntry[] = [];
	let offset = centralDirectoryOffset;

	for (let index = 0; index < entriesCount; index++) {
		if (offset + 46 > buffer.byteLength || view.getUint32(offset, true) !== centralSignature) break;
		const flags = view.getUint16(offset + 8, true);
		const method = view.getUint16(offset + 10, true);
		const modifiedTime = view.getUint16(offset + 12, true);
		const modifiedDate = view.getUint16(offset + 14, true);
		const compressedSize = view.getUint32(offset + 20, true);
		const size = view.getUint32(offset + 24, true);
		const nameLength = view.getUint16(offset + 28, true);
		const extraLength = view.getUint16(offset + 30, true);
		const commentLength = view.getUint16(offset + 32, true);
		const nameStart = offset + 46;
		const name = decodeZipString(bytes.slice(nameStart, nameStart + nameLength), Boolean(flags & 0x0800));

		entries.push({
			name,
			type: name.endsWith("/") ? "directory" : "file",
			size,
			compressedSize,
			modified: getDosDateTime(modifiedTime, modifiedDate),
			compression: getCompressionName(method),
		});

		offset = nameStart + nameLength + extraLength + commentLength;
	}

	return entries;
};

const parseCsvLine = (line: string, delimiter: string): string[] => {
	const cells: string[] = [];
	let value = "";
	let quoted = false;

	for (let i = 0; i < line.length; i++) {
		const char = line[i];
		const next = line[i + 1];
		if (char === '"' && quoted && next === '"') {
			value += '"';
			i++;
			continue;
		}
		if (char === '"') {
			quoted = !quoted;
			continue;
		}
		if (char === delimiter && !quoted) {
			cells.push(value);
			value = "";
			continue;
		}
		value += char;
	}
	cells.push(value);
	return cells;
};

const parseCsv = (text: string): TParsedCsv => {
	const lines = text
		.replace(/^\uFEFF/, "")
		.split(/\r?\n/)
		.filter(line => line.trim() !== "");
	if (!lines.length) throw new Error(msg("csvFileIsEmpty"));
	const delimiter = (lines[0].match(/;/g)?.length ?? 0) >= (lines[0].match(/,/g)?.length ?? 0) ? ";" : ",";
	const headers = parseCsvLine(lines[0], delimiter).map((header, index) => header.trim() || msg("templateColumn_994c1e75", { v0: index + 1 }));
	const rows = lines.slice(1).map(line => {
		const cells = parseCsvLine(line, delimiter);
		return headers.reduce<Record<string, string>>((acc, header, index) => {
			acc[header] = (cells[index] ?? "").trim();
			return acc;
		}, {});
	});
	return { headers, rows, delimiter };
};

const parseJsonLike = (text: string): unknown | null => {
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
};

const isFooterMarker = (value: unknown) => typeof value === "string" && /^-----[^-]+:footer-----$/.test(value.trim());

const parseFooterMarker = (value: unknown) => {
	if (!isFooterMarker(value)) return undefined;
	return String(value)
		.trim()
		.replace(/^-----/, "")
		.replace(/:footer-----$/, "");
};

const parseMetaValue = (value: unknown): [string, string] | null => {
	if (typeof value !== "string") return null;
	const match = value.trim().match(/^([A-Za-z_][\w -]*)=(.*)$/);
	if (!match) return null;
	return [match[1].trim(), match[2].trim()];
};

const createRowsPreview = (rows: Record<string, unknown>[]): TDataRowsPreview => {
	const dataRows: Record<string, unknown>[] = [];
	const footerRows: Record<string, unknown>[] = [];
	const meta: Record<string, string> = {};
	let footerType: string | undefined;

	for (const row of rows) {
		const values = Object.values(row ?? {}).filter(value => value !== undefined && value !== null && value !== "");
		const marker = values.map(parseFooterMarker).find(Boolean);
		const metaPairs = values.map(parseMetaValue).filter((pair): pair is [string, string] => Boolean(pair));
		const isMetaOnlyRow = values.length > 0 && metaPairs.length === values.length;

		if (marker || isMetaOnlyRow) {
			footerRows.push(row);
			if (marker) footerType = marker;
			for (const [key, value] of metaPairs) meta[key] = value;
			continue;
		}

		dataRows.push(row);
	}

	const headers = dataRows.reduce<string[]>((acc, row) => {
		Object.keys(row ?? {}).forEach(key => {
			if (!acc.includes(key)) acc.push(key);
		});
		return acc;
	}, []);

	return { rows: dataRows, footerRows, footerType, meta, headers };
};

const DataRowsTable = ({ rows, headers }: { rows: Record<string, unknown>[]; headers: string[] }) => { useLocale(); return ((
	rows.length === 1 ? <FileRecordPreview row={rows[0]} headers={headers} /> :
	<Box
		sx={{
			width: "100%",
			maxWidth: "100%",
			minWidth: 0,
			maxHeight: "calc(85vh - 220px)",
			overflow: "auto",
			scrollbarGutter: "stable",
			overscrollBehavior: "contain",
		}}>
		<Table<Record<string, unknown>>
			data={rows}
			order={headers}
			headers={headers.map(header => ({
				value: header,
				gridColumn: "minmax(140px, 1fr)",
				sx: { position: "sticky", top: 0, zIndex: 2, backgroundColor: viewerColors.header },
			}))}
			gridTemplateColumns={`repeat(${headers.length}, minmax(140px, 1fr))`}
			headerFactory={(_header, index) => ({
				content: <span title={headers[index]} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", fontSize: "12px", fontWeight: 600, textTransform: "uppercase" }}>{headers[index].replaceAll("_", " ")}</span>,
			})}
			cellPadding="8px 10px"
			headPadding="8px 10px"
			sx={{ minWidth: headers.length * 140 + "px" }}
			cellFactory={(_row, cell) => ({
				content: <CellText value={cell} />,
				sx: { minWidth: 0, overflow: "hidden" },
			})}
		/>
	</Box>
)); };

const parseXml = (text: string) => {
	const doc = new DOMParser().parseFromString(text, "application/xml");
	const errorNode = doc.querySelector("parsererror");
	if (errorNode) throw new Error(msg("xmlParsingFailed"));
	return doc;
};

const getXmlText = (root: Document, selector: string) => root.querySelector(selector)?.textContent?.trim() ?? "";

const getFirstXmlText = (root: Document, selectors: string[]) => {
	for (const selector of selectors) {
		const value = getXmlText(root, selector);
		if (value) return value;
	}
	return "";
};

const getXmlPairs = (root: Element) =>
	Array.from(root.children)
		.map(child => ({ label: child.tagName.replaceAll("_", " "), value: child.textContent?.trim() ?? "" }))
		.filter(pair => pair.value && pair.value.length < 160);

const getXmlRows = (root: Document) => {
	const rows = Array.from(root.querySelectorAll("data ROW")).map(row =>
		Array.from(row.children).reduce<Record<string, unknown>>((acc, child) => {
			acc[child.tagName] = child.textContent?.trim() ?? "";
			return acc;
		}, {}),
	);

	const headers = rows.reduce<string[]>((acc, row) => {
		Object.entries(row).forEach(([key, value]) => {
			if (value !== "" && !acc.includes(key)) acc.push(key);
		});
		return acc;
	}, []);

	return { rows, headers };
};

const splitReportCards = (text: string): TReportCard[] => {
	const chunks = text
		.split(/\n(?=1?[A-Z0-9]{2,}[-\w]*\s{5,})/g)
		.map(chunk => chunk.trimEnd())
		.filter(Boolean);

	return chunks.map((chunk, index) => {
		const lines = chunk.split(/\r?\n/);
		const titleLine = lines.find(line => /MASTERCARD|VISA|REPORT|SETTLEMENT|ACKNOWLEDGEMENT|NOTIFICATION/i.test(line)) ?? lines[0] ?? msg("templateReport_1aa9e0aa", { v0: index + 1 });
		const meta = lines
			.slice(0, 10)
			.filter(line => /RUN DATE|PAGE NO|MEMBER ID|CURRENCY CODE|FILE ID|Report Date|Settl\. Date|Reporting for/i.test(line))
			.map(line => line.trim())
			.filter(Boolean);
		return { title: titleLine.trim(), meta, body: chunk };
	});
};

const parseVisaSettlement = (text: string) =>
	text
		.split(/\n(?=NATIONAL SETTLEMENT)/g)
		.map(section => section.trim())
		.filter(section => section.includes("NATIONAL SETTLEMENT"))
		.map(section => {
			const reporting = section.match(/Reporting for:\s+(.+?)\s{2,}Settlement Currency Code:\s+(\w+)/);
			const settlDate = section.match(/Settl\. Date:\s+([0-9.]+)/);
			const funds = section.match(/Funds Transfer Amount\s+([-0-9.,]+)/);
			const net = section.match(/Net Settlement Amount \(Acquirer\)\s+([-0-9.,]+)/);
			return {
				reportingFor: reporting?.[1]?.trim() ?? "",
				currency: reporting?.[2]?.trim() ?? "",
				settlementDate: settlDate?.[1]?.trim() ?? "",
				netAmount: net?.[1]?.trim() ?? "",
				fundsTransferAmount: funds?.[1]?.trim() ?? "",
				raw: section,
			};
		});

const SummaryGrid = ({ children }: { children: ReactNode }) => { useLocale(); return ((
	<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }}>{children}</Box>
)); };

const SummaryItem = ({ title, value }: { title: string; value: ReactNode }) => { useLocale(); return ((
	<VBox
		gap="4px"
		sx={{
			backgroundColor: viewerColors.header,
			border: `1px solid ${viewerColors.border}`,
			borderRadius: "6px",
			padding: "10px 12px",
			minWidth: 0,
		}}>
		<Typo variant="caption" sx={{ color: viewerColors.muted, textTransform: "uppercase", fontWeight: 700 }}>
			{title}
		</Typo>
		<Typo variant="medium" sx={{ color: viewerColors.text, wordBreak: "break-word" }}>
			{value || "---"}
		</Typo>
	</VBox>
)); };

const Section = ({ title, children }: { title: string; children: ReactNode }) => { useLocale(); return ((
	<VBox gap="10px" sx={{ minWidth: 0 }}>
		<Typo variant="headMedium" sx={{ color: viewerColors.text }}>
			{title}
		</Typo>
		{children}
	</VBox>
)); };

const CellText = ({ value }: { value: unknown }) => {
	useLocale();
	const text = typeof value === "object" ? JSON.stringify(value) : String(value ?? "");
	return (
		<span
			title={text}
			style={{
				display: "block",
				minWidth: 0,
				maxWidth: "100%",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "nowrap",
				fontSize: "12px",
				lineHeight: "17px",
				color: "#4b5563",
			}}>
			{text}
		</span>
	);
};

const TableScroll = ({ children }: { children: ReactNode }) => { useLocale(); return ((
	<Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto", paddingBottom: "14px" }}>{children}</Box>
)); };

const EmptyDataState = ({ meta }: { meta: Record<string, string> }) => { useLocale(); return ((
	<VBox
		center
		gap="8px"
		sx={{
			minHeight: "150px",
			border: `1px dashed ${viewerColors.border}`,
			borderRadius: "6px",
			backgroundColor: "#fbfcfd",
			textAlign: "center",
			padding: "22px",
		}}>
		<Typo variant="headMedium" sx={{ color: viewerColors.text }}>{msg("noDataRows")}</Typo>
		<Typo variant="medium" sx={{ color: viewerColors.muted }}>
			{meta.timestamp ? msg("templateGeneratedAt_1d4874bd", { v0: meta.timestamp }) : msg("theFileContainsFooterMetadataOnly")}
		</Typo>
	</VBox>
)); };

const CsvViewer = ({ parsed }: { parsed: TParsedCsv }) => {
	useLocale();
	const preview = createRowsPreview(parsed.rows);
	const visibleHeaders = preview.headers.length ? preview.headers : parsed.headers;

	return (
		<VBox gap="16px" sx={{ minWidth: 0 }}>
			{preview.rows.length ? <DataRowsTable rows={preview.rows} headers={visibleHeaders} /> : <EmptyDataState meta={preview.meta} />}
		</VBox>
	);
};

const JsonViewer = ({ data }: { data: unknown }) => {
	useLocale();
	const rows =
		typeof data === "object" && data && Array.isArray((data as { data?: unknown }).data)
			? ((data as { data: unknown[] }).data as Record<string, unknown>[])
			: null;

	if (rows) {
		const preview = createRowsPreview(rows);

		return (
			<VBox gap="16px" sx={{ minWidth: 0 }}>
				{preview.rows.length ? <DataRowsTable rows={preview.rows} headers={preview.headers} /> : <EmptyDataState meta={preview.meta} />}
			</VBox>
		);
	}

	return <Pre data={data} wrap />;
};

const XmlViewer = ({ text }: { text: string }) => {
	useLocale();
	const doc = parseXml(text);
	const request = doc.querySelector("request_parameters");
	const pairs = request ? getXmlPairs(request).slice(0, 24) : [];
	const data = getXmlRows(doc);
	const summaryItems = [
		{ title: msg("owner"), value: getXmlText(doc, "identification owner") },
		{ title: msg("report"), value: getFirstXmlText(doc, ["request_parameters report_name", "identification id"]) },
		{ title: msg("periodFrom"), value: getFirstXmlText(doc, ["request_parameters bus_date_FROM", "request_parameters f_settl_date_FROM"]) },
		{ title: msg("periodTo"), value: getFirstXmlText(doc, ["request_parameters bus_date_TO", "request_parameters f_settl_date_TO"]) },
		{ title: msg("executed"), value: getXmlText(doc, "request_parameters execute-date") },
		{ title: msg("rows"), value: data.rows.length ? String(data.rows.length) : "" },
	].filter(item => item.value);

	return (
		<VBox gap="16px">
			{summaryItems.length ? (
				<SummaryGrid>
					{summaryItems.map(item => (
						<SummaryItem key={item.title} title={item.title} value={item.value} />
					))}
				</SummaryGrid>
			) : null}
			{data.rows.length ? (
				<Section title={msg("data")}>
					<DataRowsTable rows={data.rows} headers={data.headers} />
				</Section>
			) : (
				<EmptyDataState meta={{}} />
			)}
			{pairs.length ? (
				<Section title={msg("requestParameters")}>
					<TableScroll>
						<Table<{ label: string; value: string }>
							data={pairs}
							order={["label", "value"]}
							headers={[
								{ value: "label", gridColumn: "minmax(180px, max-content)" },
								{ value: "value", gridColumn: "minmax(0, 1fr)" },
							]}
							gridTemplateColumns="minmax(180px, max-content) minmax(0, 1fr)"
							cellPadding="8px 10px"
							headPadding="8px 10px"
						/>
					</TableScroll>
				</Section>
			) : null}
		</VBox>
	);
};

const VisaTextViewer = ({ text }: { text: string }) => {
	useLocale();
	const settlements = parseVisaSettlement(text);
	return (
		<VBox gap="16px">
			<SummaryGrid>
				<SummaryItem title={msg("report")} value="Visa VSS Settlement Detail" />
				<SummaryItem title={msg("sections")} value={settlements.length} />
			</SummaryGrid>
			{settlements.length ? (
				<Table<(typeof settlements)[number]>
					data={settlements}
					order={["reportingFor", "currency", "settlementDate", "netAmount", "fundsTransferAmount"]}
					headers={[
						{ value: "reportingFor", gridColumn: "minmax(180px, 1fr)" },
						{ value: "currency" },
						{ value: "settlementDate" },
						{ value: "netAmount" },
						{ value: "fundsTransferAmount" },
					]}
					gridTemplateColumns="minmax(180px, 1fr) auto auto auto auto"
					cellPadding="8px 10px"
					headPadding="8px 10px"
				/>
			) : null}
			<Pre data={text} wrap />
		</VBox>
	);
};

const TextReportViewer = ({ text }: { text: string }) => {
	useLocale();
	const cards = splitReportCards(text);
	const noDataCount = cards.filter(card => /NO DATA TO REPORT|No transactions found/i.test(card.body)).length;

	return (
		<VBox gap="16px">
			<SummaryGrid>
				<SummaryItem title={msg("reports")} value={cards.length} />
				<SummaryItem title={msg("noDataSections")} value={noDataCount} />
			</SummaryGrid>
			{cards.map((card, index) => (
				<VBox key={index} gap="10px" sx={{ border: `1px solid ${viewerColors.border}`, borderRadius: "6px", padding: "12px", minWidth: 0 }}>
					<Typo variant="medium" bold sx={{ color: viewerColors.text }}>
						{card.title}
					</Typo>
					{card.meta.length ? (
						<VBox gap="4px">
							{card.meta.map((line, metaIndex) => (
								<Typo key={metaIndex} variant="caption" sx={{ color: viewerColors.muted, wordBreak: "break-word" }}>
									{line}
								</Typo>
							))}
						</VBox>
					) : null}
					<Pre data={card.body} wrap />
				</VBox>
			))}
		</VBox>
	);
};

const PdfViewer = ({ blob, fileName }: { blob: Blob; fileName: string }) => {
	useLocale();
	const [url, setUrl] = useState("");

	useEffect(() => {
		const objectUrl = URL.createObjectURL(blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" }));
		setUrl(objectUrl);
		return () => URL.revokeObjectURL(objectUrl);
	}, [blob]);

	if (!url) {
		return (
			<VBox gap="14px" center sx={{ minHeight: "45vh" }}>
				<Typo variant="headMedium">{msg("loadingPdf")}</Typo>
				<Box sx={{ width: "260px" }}>
					<ProgressBar busy />
				</Box>
			</VBox>
		);
	}

	return (
		<Box
			sx={{
				height: "100%",
				minHeight: 0,
				border: `1px solid ${viewerColors.border}`,
				borderRadius: "6px",
				overflow: "hidden",
				backgroundColor: "#f8fafc",
			}}>
			<iframe
				title={msg("templatePdfPreview_79c1ea6f", { v0: fileName })}
				src={url}
				style={{
					width: "100%",
					height: "100%",
					border: 0,
					display: "block",
					backgroundColor: "#fff",
				}}
			/>
		</Box>
	);
};

const ZipViewer = ({ blob }: { blob: Blob }) => {
	useLocale();
	const [entries, setEntries] = useState<TZipEntry[]>([]);
	const [busy, setBusy] = useState(true);
	const [error, setError] = useState<IError | undefined>();

	useEffect(() => {
		let active = true;
		setBusy(true);
		setError(undefined);
		setEntries([]);

		parseZipEntries(blob)
			.then(result => {
				if (active) setEntries(result);
			})
			.catch(e => {
				if (active) setError(getError(e instanceof Error ? e.message : msg("zipParsingFailed")));
			})
			.finally(() => {
				if (active) setBusy(false);
			});

		return () => {
			active = false;
		};
	}, [blob]);

	if (busy) {
		return (
			<VBox gap="14px" center sx={{ minHeight: "45vh" }}>
				<Typo variant="headMedium">{msg("readingZip")}</Typo>
				<Box sx={{ width: "260px" }}>
					<ProgressBar busy />
				</Box>
			</VBox>
		);
	}

	if (error) return <ErrorView error={error} />;

	const files = entries.filter(entry => entry.type === "file");
	const directories = entries.length - files.length;
	const totalSize = files.reduce((sum, entry) => sum + entry.size, 0);

	return (
		<VBox gap="16px">
			<SummaryGrid>
				<SummaryItem title={msg("files")} value={files.length} />
				<SummaryItem title={msg("folders")} value={directories} />
				<SummaryItem title={msg("totalSize")} value={formatBytes(totalSize)} />
			</SummaryGrid>
			{entries.length ? (
				<Table<TZipEntry>
					data={entries}
					order={["type", "name", "size", "modified"]}
					headers={[
						{ value: "type", gridColumn: "90px" },
						{ value: "name", gridColumn: "minmax(260px, 1fr)" },
						{ value: "size", gridColumn: "110px" },
						{ value: "modified", gridColumn: "170px" },
					]}
					gridTemplateColumns="90px minmax(260px, 1fr) 110px 170px"
					cellPadding="8px 10px"
					headPadding="8px 10px"
					cellFactory={(row, cell, _index, _key, name) => {
						if (name === "modified") return { content: <DateTimeTooltip value={row.modified}>{formatDisplayDateTime(row.modified, { fallback: "" })}</DateTimeTooltip> };
						if (name === "size") return { content: <CellText value={formatBytes(Number(cell))} /> };
						if (name === "type") return { content: <CellText value={row.type === "directory" ? "Folder" : "File"} /> };
						return { content: <CellText value={cell} />, sx: { minWidth: 0, overflow: "hidden" } };
					}}
				/>
			) : (
				<EmptyDataState meta={{}} />
			)}
		</VBox>
	);
};

const UnsupportedViewer = ({ onDownload, busy }: { onDownload: () => void; busy: boolean }) => { useLocale(); return ((
	<VBox center gap="14px" sx={{ minHeight: "45vh", textAlign: "center" }}>
		<Typo variant="headMedium">{msg("previewIsNotAvailableForThisFile")}</Typo>
		<Typo variant="medium" sx={{ color: viewerColors.muted }}>{msg("youCanDownloadIt")}</Typo>
		<Button
			variant="icon"
			disabled={busy}
			title={msg("download")}
			onClick={onDownload}
			sx={{
				width: "52px",
				height: "52px",
				borderRadius: "6px",
				border: `1px solid ${viewerColors.border}`,
				backgroundColor: viewerColors.header,
			}}>
			{busy ? <Spinner size={24} color={viewerColors.text} /> : <IconDownload size={30} color={viewerColors.text} />}
		</Button>
	</VBox>
)); };

const SafePreview = ({ fileName, text }: { fileName: string; text: string }) => {
	useLocale();
	try {
		const json = parseJsonLike(text);
		if (json) return <JsonViewer data={json} />;

		const ext = getFileExtension(fileName);
		const trimmed = text.trimStart();
		if (ext === "csv") return <CsvViewer parsed={parseCsv(text)} />;
		if (ext === "xml" || trimmed.startsWith("<?xml") || trimmed.startsWith("<report_info")) return <XmlViewer text={text} />;
		if (/VISA VSS/i.test(text)) return <VisaTextViewer text={text} />;
		if (/MASTERCARD|IP7\d{5}|IP1\d{5}|SETTLEMENT POSITION REPORT/i.test(text)) return <TextReportViewer text={text} />;
		return <Pre data={text} wrap />;
	} catch (e) {
		return (
			<VBox gap="12px">
				<ErrorView error={getError(e instanceof Error ? e.message : msg("previewParsingFailed"))} />
				<Pre data={text} wrap />
			</VBox>
		);
	}
};

const footerSx: RecursiveCSS = {
	borderTop: `1px solid ${viewerColors.border}`,
	padding: "12px 0 0",
	alignItems: "center",
};

const footerButtonSx: RecursiveCSS = {
	minHeight: "40px",
	height: "40px",
	padding: "0 16px",
	borderRadius: "6px",
	alignItems: "center",
	boxSizing: "border-box",
};

export const DectaFilesPopup = () => {
	const localeRevision = useLocale();
	const requestData = S_DECTA_FILES_POPUP.use();
	const [loadedFile, setLoadedFile] = useState<TLoadedFileState | null>(null);
	const [busy, setBusy] = useState(false);
	const [downloadBusy, setDownloadBusy] = useState(false);
	const [error, setError] = useState<IError | undefined>();
	const currentKey = requestData ? getRequestKey(requestData) : "";
	const isCurrentFileLoaded = loadedFile?.key === currentKey;
	const currentBlob = isCurrentFileLoaded ? loadedFile.blob : null;
	const currentText = isCurrentFileLoaded ? loadedFile.text : "";
	const isCurrentPdf = requestData ? isPdfFile(requestData.name) : false;
	const isCurrentZip = requestData ? isZipFile(requestData.name) : false;
	const isCurrentCsv = requestData ? getFileExtension(requestData.name) === "csv" : false;
	const showLoading = busy || (!!requestData && !isCurrentFileLoaded && !error);

	useEffect(() => {
		if (!requestData) return;
		let active = true;
		const key = getRequestKey(requestData);
		setLoadedFile(null);
		setError(undefined);
		setBusy(true);

		requestData
			.load()
			.then(async loadedBlob => {
				if (!active) return;
				const loadedText = requestData.previewable && !isPdfFile(requestData.name) && !isZipFile(requestData.name) ? await readBlobAsText(loadedBlob) : "";
				if (!active) return;
				setLoadedFile({ key, blob: loadedBlob, text: loadedText });
			})
			.catch(e => {
				if (!active) return;
				setError(getError(e instanceof Error ? e.message : msg("errorLoadingFile")));
			})
			.finally(() => {
				if (active) setBusy(false);
			});

		return () => {
			active = false;
		};
	}, [requestData]);

	const closePopup = () => {
		S_DECTA_FILES_POPUP.invoke(null);
	};

	const handleDownload = async () => {
		if (!requestData) return;
		setDownloadBusy(true);
		try {
			const sourceBlob = currentBlob ?? (await requestData.load());
			const sourceText = isCurrentCsv && !currentText ? await readBlobAsText(sourceBlob) : currentText;
			FileSaver.saveAs(await prepareFileDownloadBlob(requestData.name, sourceBlob, sourceText), requestData.name);
			if (!currentBlob) setLoadedFile({ key: currentKey, blob: sourceBlob, text: sourceText });
		} catch (e) {
			setError(getError(e instanceof Error ? e.message : msg("errorDownloadingFile")));
		} finally {
			setDownloadBusy(false);
		}
	};

	const content = useMemo(() => {
		if (!requestData) return null;
		if (showLoading) {
			return (
				<VBox gap="14px" center sx={{ minHeight: "45vh" }}>
					<Typo variant="headMedium">{msg("loadingFile")}</Typo>
					<Box sx={{ width: "260px" }}>
						<ProgressBar busy />
					</Box>
				</VBox>
			);
		}
		if (error) return <ErrorView error={error} />;
		if (!requestData.previewable) return <UnsupportedViewer onDownload={handleDownload} busy={downloadBusy} />;
		if (isCurrentPdf && currentBlob) return <PdfViewer blob={currentBlob} fileName={requestData.name} />;
		if (isCurrentZip && currentBlob) return <ZipViewer blob={currentBlob} />;
		return <SafePreview fileName={requestData.name} text={currentText} />;
	}, [localeRevision, currentBlob, currentText, downloadBusy, error, isCurrentPdf, isCurrentZip, requestData, showLoading]);

	if (!requestData) return null;

	return (
		<Panel
			sx={{
				minWidth: "600px",
				width: "85vw",
				height: "85vh",
				padding: "0",
				maxWidth: "1100px",
			}}
			decorator="absolute"
			label={msg("templateFilePreview_fc50e087", { v0: requestData.name })}
			onClose={closePopup}>
			<VBox gap="14px" sx={{ height: "100%", minHeight: 0 }}>
				<Box
					sx={{
						minHeight: 0,
						flexGrow: 1,
						overflowY: isCurrentPdf ? "hidden" : "auto",
						overflowX: isCurrentPdf || isCurrentCsv ? "hidden" : "auto",
						padding: isCurrentPdf ? "0" : "0 12px 8px 0",
						scrollbarGutter: isCurrentPdf ? undefined : "stable",
					}}>
					{content}
				</Box>
				{requestData.previewable ? (
					<HBox gap="12px" sx={footerSx}>
						<Spacer grow />
					<Button variant="secondarySmall" sx={footerButtonSx} disabled={downloadBusy || busy} onClick={handleDownload}>
						{downloadBusy ? <Spinner size={16} color={viewerColors.text} /> : <IconDownload size={18} color={viewerColors.text} />}
						{downloadBusy ? msg("downloading") : msg("download")}
						</Button>
						<Button title={msg("close")} sx={footerButtonSx} onClick={closePopup} />
					</HBox>
				) : null}
			</VBox>
		</Panel>
	);
};
